/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type Ref } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonProps } from '../common/types/shared-types';

import { RovoLogo } from './components/RovoLogo';

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xxlarge'),
	},
	boxStyles: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
});

type AIButtonProps = Pick<AISmartButtonProps, 'isDisabled'> & {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	ref?: React.Ref<HTMLButtonElement>;
};

export const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
	({ onClick, isDisabled, onMouseEnter, onMouseLeave }, ref: Ref<HTMLButtonElement>) => {
		return (
			<div css={atlaskitCssStyles.boxStyles}>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<VisuallyHidden> Rovo Button </VisuallyHidden>
					<RovoLogo />
				</Pressable>
			</div>
		);
	},
);
